import type { ComponentType } from 'react'

import {
	BulletsBlock,
	DividerBlock,
	HeadingBlock,
	ImageBlock,
	KpiBlock,
	KpisBlock,
	TextBlock,
} from './generic/GenericBlocks'
import {
	LocationCoverBlock,
	LocationDemographicsBlock,
	LocationFactsBlock,
	LocationNotesBlock,
	LocationOtherRetailersBlock,
	LocationPhotoBlock,
	LocationPhotoGridBlock,
	LocationProjectsBlock,
	LocationTenantsBlock,
} from './location/LocationBlocks'
import { EvaluationSitesMapBlock, LocationMapBlock, PortfolioMapBlock } from './map/MapBlocks'
import { FinancialFiguresBlock, IncomeChartBlock, PortfolioFinancialsBlock, SiteFinancialsBlock } from './financial/FinancialBlocks'
import {
	EvaluationComparisonMatrixBlock,
	EvaluationOverviewBlock,
	EvaluationProsConsBlock,
	EvaluationRankingBlock,
	EvaluationReadinessBlock,
	EvaluationRiskHeatMapBlock,
	EvaluationScoreProfileBlock,
	EvaluationTopRisksBlock,
	EvaluationWeightsBlock,
} from './evaluation/EvaluationBlocks'
import { ExplorerTableBlock } from './explorer/ExplorerTableBlock'
import { pageCount, readExplorerTable } from './explorer/explorerTable'
import { ProjectPartnersBlock, ProjectTaskListBlock, ProjectTaskSummaryBlock } from './project/ProjectBlocks'
import {
	PortfolioCoverBlock,
	PortfolioGeoBlock,
	PortfolioLocationsTableBlock,
	PortfolioMosaicBlock,
	PortfolioTopLocationsBlock,
} from './portfolio/PortfolioBlocks'

/**
 * Element key to renderer (MYD-495).
 *
 * The server decides which elements exist; this decides how each one looks.
 * Adding an element is one `defineElement` entry on the server plus one line
 * here. A key with no entry renders "Not supported here" rather than failing,
 * so a deck saved by a newer server still opens in an older tab.
 *
 * Every renderer is pure: payload in, JSX out.
 */
export interface BlockRenderer {
	component: ComponentType<{ payload: any }>
	/**
	 * Stretch to fill the region's height. For images and maps, which have no
	 * natural height. Text-like blocks keep their own height so a heading does
	 * not float in a sea of whitespace.
	 */
	fill?: boolean
	/**
	 * How many slides this payload fills, when it may run past one (MYD-504:
	 * an Explorer table set to continue). The presenter adds a continuation
	 * slide for each page past the first, and hands the renderer its page as
	 * `payload.page`. Absent means one.
	 */
	pages?: (payload: unknown) => number
}

export const BLOCK_RENDERERS: Record<string, BlockRenderer> = {
	'generic.heading': { component: HeadingBlock },
	'generic.text': { component: TextBlock },
	'generic.bullets': { component: BulletsBlock },
	'generic.kpi': { component: KpiBlock, fill: true },
	'generic.kpis': { component: KpisBlock },
	'generic.image': { component: ImageBlock, fill: true },
	'generic.divider': { component: DividerBlock },

	// MYD-499
	'location.cover': { component: LocationCoverBlock, fill: true },
	'location.facts': { component: LocationFactsBlock },
	'location.tenants': { component: LocationTenantsBlock },
	'location.photo': { component: LocationPhotoBlock, fill: true },
	'location.photoGrid': { component: LocationPhotoGridBlock, fill: true },
	'location.demographics': { component: LocationDemographicsBlock, fill: true },
	'location.otherRetailers': { component: LocationOtherRetailersBlock },
	'location.notes': { component: LocationNotesBlock },
	'location.projects': { component: LocationProjectsBlock },

	// MYD-500
	'location.map': { component: LocationMapBlock, fill: true },
	'portfolio.map': { component: PortfolioMapBlock, fill: true },
	'evaluation.sitesMap': { component: EvaluationSitesMapBlock, fill: true },

	// MYD-503
	'location.financialKpis': { component: FinancialFiguresBlock },
	'location.incomeChart': { component: IncomeChartBlock, fill: true },
	'portfolio.rolledUpFinancials': { component: PortfolioFinancialsBlock },
	'evaluation.siteFinancials': { component: SiteFinancialsBlock },

	// MYD-502
	'evaluation.overview': { component: EvaluationOverviewBlock },
	'evaluation.ranking': { component: EvaluationRankingBlock },
	'evaluation.scoreProfile': { component: EvaluationScoreProfileBlock, fill: true },
	'evaluation.weights': { component: EvaluationWeightsBlock },
	'evaluation.comparisonMatrix': { component: EvaluationComparisonMatrixBlock },
	'evaluation.riskHeatMap': { component: EvaluationRiskHeatMapBlock },
	'evaluation.topRisks': { component: EvaluationTopRisksBlock },
	'evaluation.prosCons': { component: EvaluationProsConsBlock },
	'evaluation.approvalReadiness': { component: EvaluationReadinessBlock },

	// MYD-501. The cover, details, photos, notes and key figures share their
	// location and generic counterparts' payload shapes, so they share renderers.
	'project.cover': { component: LocationCoverBlock, fill: true },
	'project.details': { component: LocationFactsBlock },
	'project.taskSummary': { component: ProjectTaskSummaryBlock },
	'project.taskList': { component: ProjectTaskListBlock },
	'project.photos': { component: LocationPhotoGridBlock, fill: true },
	'project.partners': { component: ProjectPartnersBlock },
	'project.notes': { component: LocationNotesBlock },
	'portfolio.cover': { component: PortfolioCoverBlock, fill: true },
	'portfolio.kpis': { component: KpisBlock },
	'portfolio.locationsTable': { component: PortfolioLocationsTableBlock },
	'portfolio.geoDistribution': { component: PortfolioGeoBlock },
	'portfolio.topLocations': { component: PortfolioTopLocationsBlock },
	'portfolio.photoMosaic': { component: PortfolioMosaicBlock, fill: true },

	// MYD-504
	'explorer.table': { component: ExplorerTableBlock, pages: payload => pageCount(readExplorerTable(payload)) },
}

export const rendererFor = (element: string): BlockRenderer | null =>
	Object.prototype.hasOwnProperty.call(BLOCK_RENDERERS, element) ? BLOCK_RENDERERS[element] : null
